import type { FormSelectOption } from '@practium/ui';
import { useGetSoilAnalysisById } from '@practium/features/api';
import { FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import type { UseCorrectionFormOptions } from '../../../hooks/useCorrectionForm';
import { useCorrectionForm } from '../../../hooks/useCorrectionForm';
import type { PlantingAdvice } from '../../../types';
import { MultiSelect } from '../../MultiSelect/MultiSelect';
import styles from './CorrectionForm.module.css';

export interface CorrectionFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface CorrectionFormProps extends UseCorrectionFormOptions {
	viewData?: PlantingAdvice;
	soilAnalysisOptions?: FormSelectOption[];
	onLoadingChange?: (loading: boolean) => void;
	onPropertyChange?: (propertyId: string) => void;
}

const getDisplayValue = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const label = record.name ?? record.title ?? record._id ?? record.id;
		return typeof label === 'string' ? label : '';
	}
	return String(value);
};

const getReferenceId = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	const record = value as Record<string, unknown>;
	const id = record._id ?? record.id;
	return typeof id === 'string' ? id : '';
};

const getReferenceName = (value: unknown): string => {
	if (!value || typeof value !== 'object') return '';
	const record = value as Record<string, unknown>;
	const name = record.name ?? record.title;
	return typeof name === 'string' ? name : '';
};

export const CorrectionForm = forwardRef<CorrectionFormRef, CorrectionFormProps>(
	function CorrectionForm({ viewData, onLoadingChange, onPropertyChange, ...hookProps }, ref) {
		const {
			form,
			errors,
			isLoading,
			set,
			handleSubmit,
			isDirty,
			propOptions,
			areaOptions,
			sectorOptions,
			soilAnalysisOptions,
		} = useCorrectionForm(hookProps);
		const embeddedSoilAnalysisName =
			viewData?.soilAnalysisName || getReferenceName(viewData?.soilAnalysis);
		const soilAnalysisId = getReferenceId(viewData?.soilAnalysis);
		const { data: soilAnalysis, isLoading: isLoadingViewSoilAnalysis } = useGetSoilAnalysisById(
			soilAnalysisId,
			{
				enabled: !!viewData && !embeddedSoilAnalysisName,
			}
		);
		const viewSoilAnalysisName =
			embeddedSoilAnalysisName ||
			soilAnalysis?.name ||
			(isLoadingViewSoilAnalysis ? 'Carregando...' : '—');

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		useEffect(() => {
			onPropertyChange?.(form.prop);
		}, [form.prop, onPropertyChange]);

		if (viewData) {
			return (
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Identificação</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Identificação">
									<span className={styles.viewText}>{viewData.name || '—'}</span>
								</FormField>
								<FormField label="Propriedade">
									<span className={styles.viewText}>
										{viewData.propName || getDisplayValue(viewData.prop) || '—'}
									</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Área">
									<span className={styles.viewText}>
										{viewData.areaName || getDisplayValue(viewData.area) || '—'}
									</span>
								</FormField>
								<FormField label="Setores">
									<span className={styles.viewText}>
										{viewData.sectors.map(getDisplayValue).filter(Boolean).join(', ') || '—'}
									</span>
								</FormField>
							</div>
							<div className={styles.halfLeft}>
								<div className={styles.halfLeftField}>
									<FormField label="Análise de solo da área">
										<span className={styles.viewText}>{viewSoilAnalysisName}</span>
									</FormField>
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.form}>
				<div className={styles.section}>
					<FormSectionTitle>Identificação</FormSectionTitle>
					<div className={styles.sectionFields}>
						<div className={styles.row2}>
							<FormField label="Identificação" required error={errors.name}>
								<FormInput
									placeholder="Ex.: Recomendação Maria - 2025/2026"
									value={form.name}
									onChange={(e) => set('name', e.target.value)}
									hasError={!!errors.name}
								/>
							</FormField>
							<FormField label="Propriedade" required error={errors.prop}>
								<FormSelect
									value={form.prop}
									onChange={(v) => set('prop', v)}
									options={propOptions}
									placeholder="Selecione a propriedade"
									hasError={!!errors.prop}
								/>
							</FormField>
						</div>
						<div className={styles.row2}>
							<FormField label="Área" required error={errors.area}>
								<FormSelect
									value={form.area}
									onChange={(v) => set('area', v)}
									options={areaOptions}
									placeholder="Selecione a área"
									hasError={!!errors.area}
									disabled={!form.prop}
								/>
							</FormField>
							<FormField label="Análises de solo da área" required error={errors.soilAnalysis}>
								<FormSelect
									value={form.soilAnalysis}
									onChange={(v) => set('soilAnalysis', v)}
									options={soilAnalysisOptions}
									placeholder="Selecione a análise"
									hasError={!!errors.soilAnalysis}
									disabled={!form.area}
								/>
							</FormField>
						</div>
						<div className={styles.halfLeft}>
							<div className={styles.halfLeftField}>
								<FormField label="Setores" required error={errors.sectors}>
									<MultiSelect
										values={form.sectors}
										onChange={(v) => set('sectors', v)}
										options={sectorOptions}
										placeholder="Selecione os setores"
										hasError={!!errors.sectors}
										disabled={!form.area}
										showSelectAll
									/>
								</FormField>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
);
